import { FC, MouseEvent, useRef, useState } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import Text from "@/components/Text";
import { ReactComponent as PlusIcon } from "@/assets/icons/plus.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import GroupCreationModal from "./GroupCreationModal";
import { Box } from "@/components/Dropdown";
import { ActionsDropdownButton } from "@/components/ActionsDropdown";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { OptimisationTestsGroupResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

const Wrap = styled.div`
  display: flex;
  margin-right: auto;
  gap: 8px;
  align-self: flex-end;
`;

const GroupButton = styled.button`
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  justify-content: center;
  height: 24px;
  padding: 0 16px;
  min-width: 44px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};

  ${Text} + svg {
    margin-left: 8px;
  }
`;

const Selections = styled.div`
  position: relative;
`;

const StyledBox = styled(Box)`
  right: auto;
  left: 0;
  padding: 4px;
  width: 180px;
  top: calc(100% + 8px);
`;

const AddGroupButton = styled(GroupButton)`
  background: ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
  path {
    stroke-width: 1.5px;
  }
`;

const SelectionButton = styled(ActionsDropdownButton)`
  height: 32px;
  svg {
    width: 16px;
  }
`;

interface Props {
  setActiveGroup: React.Dispatch<
    React.SetStateAction<OptimisationTestsGroupResource | null>
  >;
  activeGroup: OptimisationTestsGroupResource | null;
  groups?: OptimisationTestsGroupResource[];
  setSelectedBatches: React.Dispatch<React.SetStateAction<number[]>>;
}

const GroupControls: FC<Props> = ({
  setActiveGroup,
  activeGroup,
  groups,
  setSelectedBatches,
}) => {
  const selectionRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  const alerts = useAlerts();

  const { id: projectId } = useParams();

  const queryClient = useQueryClient();

  const [showCreationModal, setShowCreationModal] = useState(false);
  const [editableGroup, setEditableGroup] =
    useState<OptimisationTestsGroupResource | null>(null);
  const [showGroupSelection, setShowGroupSelection] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const onAddGroupClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowCreationModal(true);
  };

  useOnClickOutside(selectionRef, () => setShowGroupSelection(false));
  useOnClickOutside(actionsRef, () => setShowActions(false));

  const onSelectionToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowGroupSelection((prev) => !prev);
  };

  const onSelectionClick = (group: OptimisationTestsGroupResource | null) => {
    setActiveGroup(group);
    setShowGroupSelection(false);
  };

  const onActionsToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowActions((prev) => !prev);
  };

  const onGroupDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      setShowActions(false);

      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete this group?",
        activeGroup!.name
      );

      if (!confirmed) return;

      const { id: deletedGroupId } = await api.delete<never, { id: number }>(
        `/v1/optimisation-tests-groups/${activeGroup!.id}`
      );

      setActiveGroup(null);

      queryClient.setQueryData<OptimisationTestsGroupResource[]>(
        ["tips-tests-groups", { projectId }],
        (groups) => groups?.filter((g) => g.id !== deletedGroupId)
      );

      alerts.success("Group successfully deleted");
    } catch (err) {
      alerts.error();
    }
  };

  const onRename = () => {
    setShowActions(false);
    setEditableGroup(activeGroup);
    setShowCreationModal(true);
  };

  return (
    <Wrap>
      <Selections>
        <GroupButton onClick={onSelectionToggle}>
          <Text as="span" $type="smallHeadline">
            {activeGroup?.name ?? "Original"}
          </Text>
          <svg
            width="8"
            height="5"
            viewBox="0 0 8 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L4 4L7 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </GroupButton>
        {showGroupSelection && (
          <StyledBox ref={selectionRef}>
            <SelectionButton
              as="button"
              key={0}
              onClick={() => {
                onSelectionClick(null);
                setSelectedBatches([]);
              }}
            >
              Original
            </SelectionButton>
            {groups?.map((group) => (
              <SelectionButton
                as="button"
                key={group.id}
                onClick={() => {
                  onSelectionClick(group);
                  setSelectedBatches([]);
                }}
              >
                {group.name}
              </SelectionButton>
            ))}
          </StyledBox>
        )}
      </Selections>
      {!!activeGroup && (
        <Selections>
          <GroupButton onClick={onActionsToggle}>
            <svg
              width="12"
              height="13"
              viewBox="0 0 12 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 3.5C6.55229 3.5 7 3.05228 7 2.5C7 1.94771 6.55229 1.5 6 1.5C5.44772 1.5 5 1.94771 5 2.5C5 3.05228 5.44772 3.5 6 3.5Z"
                fill="currentColor"
              />
              <path
                d="M6 7.5C6.55229 7.5 7 7.05228 7 6.5C7 5.94771 6.55229 5.5 6 5.5C5.44772 5.5 5 5.94771 5 6.5C5 7.05228 5.44772 7.5 6 7.5Z"
                fill="currentColor"
              />
              <path
                d="M6 11.5C6.55229 11.5 7 11.0523 7 10.5C7 9.94772 6.55229 9.5 6 9.5C5.44772 9.5 5 9.94772 5 10.5C5 11.0523 5.44772 11.5 6 11.5Z"
                fill="currentColor"
              />
            </svg>
          </GroupButton>
          {showActions && (
            <StyledBox ref={actionsRef}>
              <SelectionButton as="button" onClick={onRename}>
                <PencilIcon />
                Rename
              </SelectionButton>
              <SelectionButton as="button" onClick={onGroupDelete}>
                <TrashIcon />
                Delete
              </SelectionButton>
            </StyledBox>
          )}
        </Selections>
      )}

      <AddGroupButton onClick={onAddGroupClick}>
        <PlusIcon />
      </AddGroupButton>
      <AnimatePresence>
        {showCreationModal &&
          (!!editableGroup ? (
            <GroupCreationModal
              editableGroup={editableGroup}
              onClose={() => {
                setShowCreationModal(false);
                setEditableGroup(null);
              }}
            />
          ) : (
            <GroupCreationModal
              onGroupCreated={onSelectionClick}
              onClose={() => setShowCreationModal(false)}
            />
          ))}
      </AnimatePresence>
    </Wrap>
  );
};

export default GroupControls;
